/**
 * Sidebar — Sidebar completo con logo, restaurant switcher, navegación,
 * toggle de colapso, versión y user menu.
 * Extraído de DashboardLayout.jsx (iter.246 · Fase 8 UX Refactor).
 *
 * Props:
 *  - sidebarOpen: bool, si está abierto en móvil.
 *  - onCloseMobile: cerrar en móvil.
 *  - collapsed: bool, sidebar colapsado en desktop.
 *  - onToggleCollapsed: toggle colapso.
 *  - restaurants, currentRestaurant, onSelectRestaurant, currentRole: para RestaurantSwitcher.
 *  - isStarter: si el usuario está en plan starter.
 *  - hasModule: función `hasModule(key)` de `usePlanFeatures`.
 *  - badges: { draftOrders, fiscalUnresolved, shoppingListsPending }.
 *  - user: usuario para UserMenu.
 *  - userGuideVisible: si mostrar link a guía PDF.
 *  - onLogout: callback logout.
 */
import { Link } from "react-router-dom";
import { ChefHat, X, PanelLeftClose, PanelLeftOpen } from "lucide-react";
import { ScrollArea } from "../ui/scroll-area";
import SidebarNav from "./SidebarNav";
import { BuildVersion } from "./BuildVersion";
import { RestaurantSwitcher } from "./RestaurantSwitcher";
import { UserMenu } from "./UserMenu";

export function Sidebar({
  sidebarOpen,
  onCloseMobile,
  collapsed,
  onToggleCollapsed,
  restaurants,
  currentRestaurant,
  onSelectRestaurant,
  currentRole,
  isStarter,
  hasModule,
  badges,
  user,
  userGuideVisible,
  onLogout,
}) {
  return (
    <aside
      className={`fixed inset-y-0 left-0 z-50 bg-card border-r border-border transform transition-all duration-200 ease-in-out
        ${sidebarOpen ? "translate-x-0" : "-translate-x-full"}
        lg:translate-x-0
        ${collapsed ? "lg:w-[68px]" : "lg:w-64"}
      `}
      data-testid="sidebar" /* z-stack-doc: L5 desktop/mobile sidebar; siblings of Radix modals which use the same level (mutually exclusive in practice) */
    >
      <div className="flex flex-col h-full">
        {/* Logo */}
        <div className="h-16 flex items-center justify-between px-3 border-b border-border">
          <Link
            to={isStarter ? "/app/customers" : "/app"}
            className="flex items-center gap-2 overflow-hidden"
          >
            <div className="w-9 h-9 bg-primary rounded-xl flex items-center justify-center flex-shrink-0">
              <ChefHat className="w-5 h-5 text-primary-foreground" />
            </div>
            {!collapsed && (
              <span className="font-heading text-lg font-semibold whitespace-nowrap">
                MenuGenius
              </span>
            )}
          </Link>
          <button
            className="lg:hidden p-2 hover:bg-muted rounded-lg"
            onClick={onCloseMobile}
            aria-label="Cerrar menú"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Restaurant Selector */}
        <RestaurantSwitcher
          restaurants={restaurants}
          currentRestaurant={currentRestaurant}
          onSelect={onSelectRestaurant}
          currentRole={currentRole}
          collapsed={collapsed}
        />

        {/* Navigation */}
        <ScrollArea className="flex-1 py-4">
          <div className={collapsed ? "px-2" : "px-3"}>
            <SidebarNav
              role={currentRole}
              restaurantId={currentRestaurant?.restaurant_id}
              collapsed={collapsed}
              isStarter={isStarter}
              hasModule={hasModule}
              badges={badges}
              onItemClick={onCloseMobile}
            />
          </div>
        </ScrollArea>

        {/* Collapse toggle (desktop only) */}
        <div className="hidden lg:flex justify-center py-2 border-t border-border">
          <button
            onClick={onToggleCollapsed}
            className="p-2 rounded-lg text-muted-foreground hover:text-foreground hover:bg-muted transition-colors"
            data-testid="sidebar-collapse-btn"
            title={collapsed ? "Expandir menú" : "Contraer menú"}
          >
            {collapsed ? (
              <PanelLeftOpen className="w-5 h-5" />
            ) : (
              <PanelLeftClose className="w-5 h-5" />
            )}
          </button>
        </div>

        {/* Build version — discreet footer */}
        <BuildVersion collapsed={collapsed} />

        {/* User */}
        <UserMenu
          user={user}
          currentRole={currentRole}
          collapsed={collapsed}
          userGuideVisible={userGuideVisible}
          onLogout={onLogout}
        />
      </div>
    </aside>
  );
}
